import { useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import { useLazyGetCategorizationOptionsQuery } from "../store/metadataApi";
import { updateCategorizationAnnotation } from "../store/metadataSlice";
import { OptionType } from "../interfaces/classifier";
import { CategorizationAnnotation } from "../../configMap/interfaces/category";
import { notification } from "antd";
import { useTranslation } from "react-i18next";

const useCategorizationDataFetcher = (needToUpdateStore = true) => {
  const { t } = useTranslation("errors");
  const dispatch = useAppDispatch();
  const { categorizationAnnotations } = useAppSelector(
    (state) => state.metadataReducer.categorizationState,
  );

  // Use a ref to avoid recreating fetchCategorizationAnnotationData
  // every time categorizationAnnotations changes in Redux.
  // This prevents a dependency loop where dispatching updates to the store
  // causes the callback to recreate, which re-triggers useEffects in callers.
  // The write has to happen during render: moving it into an effect would leave
  // the ref one commit stale for any caller that invokes
  // fetchCategorizationAnnotationData before effects flush. The value is never
  // rendered — it is only read inside the async callback below — so the staleness
  // the rule guards against cannot be observed here.
  const categorizationAnnotationsRef = useRef(categorizationAnnotations);
  // eslint-disable-next-line react-hooks/refs -- latest-value ref, read only from an async callback and never rendered
  categorizationAnnotationsRef.current = categorizationAnnotations;

  const [getCategorizationOptions, { isLoading }] =
    useLazyGetCategorizationOptionsQuery();

  const getAnnotationsToRetrieve = useCallback(
    (annotations: Array<CategorizationAnnotation>) => {
      if (!annotations?.length) {
        return [];
      }

      return annotations.filter(
        (annotation) =>
          annotation.externalDataId &&
          !annotation.visibleValue &&
          !annotation.isLoading, // Don't retrieve if already loading
      );
    },
    [],
  );

  const setLoadingState = useCallback(
    (annotations: Array<CategorizationAnnotation>) => {
      if (!needToUpdateStore) {
        return;
      }

      annotations.forEach((annotation) => {
        dispatch(
          updateCategorizationAnnotation({
            ...annotation,
            isLoading: true,
          }),
        );
      });
    },
    [dispatch, needToUpdateStore],
  );

  const processResolvedAnnotations = useCallback(
    (
      annotationsToRetrieve: Array<CategorizationAnnotation>,
      categorizationOptions: OptionType[],
    ) => {
      return annotationsToRetrieve.map((annotation) => {
        const resolved = categorizationOptions?.find(
          (item: OptionType) => item.key === annotation.externalDataId,
        );

        const updatedAnnotation = {
          ...annotation,
          visibleValue: resolved?.visibleValue,
          value: resolved?.value,
          isLoading: false,
        };

        if (needToUpdateStore) {
          dispatch(updateCategorizationAnnotation(updatedAnnotation));
        }

        return updatedAnnotation;
      });
    },
    [dispatch, needToUpdateStore],
  );

  const processFailedAnnotations = useCallback(
    (annotationsToRetrieve: Array<CategorizationAnnotation>) => {
      return annotationsToRetrieve.map((annotation) => {
        const failedAnnotation = {
          ...annotation,
          visibleValue: undefined,
          isLoading: false,
        };

        if (needToUpdateStore) {
          dispatch(updateCategorizationAnnotation(failedAnnotation));
        }

        return failedAnnotation;
      });
    },
    [dispatch, needToUpdateStore],
  );

  const mergeAnnotations = useCallback(
    (
      allAnnotations: Array<CategorizationAnnotation>,
      updatedAnnotations: Array<CategorizationAnnotation>,
    ) => {
      return allAnnotations.map((annotation) => {
        const updatedAnnotation = updatedAnnotations.find(
          (updated) =>
            updated.externalDataId === annotation.externalDataId &&
            updated.categorizationId === annotation.categorizationId &&
            updated.index === annotation.index &&
            updated.groupBlockId === annotation.groupBlockId,
        );
        return updatedAnnotation || annotation;
      });
    },
    [],
  );

  const startCategorizationLookup = useCallback(
    (annotations: Array<CategorizationAnnotation>) => {
      const recordIds = getAnnotationsToRetrieve(annotations).map(
        (annotation) => annotation.externalDataId,
      );

      if (recordIds.length === 0) {
        return undefined;
      }

      const lookup = getCategorizationOptions({ recordIds }).unwrap();
      // The caller awaits and reports it; this only avoids an unhandled rejection.
      lookup.catch(() => undefined);
      return lookup;
    },
    [getAnnotationsToRetrieve, getCategorizationOptions],
  );

  const fetchCategorizationAnnotationData = useCallback(
    async (
      passedAnnotations?: Array<CategorizationAnnotation>,
      startedLookup?: Promise<Array<OptionType>>,
    ) => {
      const allCategorizationAnnotations =
        passedAnnotations || categorizationAnnotationsRef.current;
      const annotationsToRetrieve = getAnnotationsToRetrieve(
        allCategorizationAnnotations,
      );

      if (annotationsToRetrieve.length === 0) {
        return allCategorizationAnnotations;
      }

      setLoadingState(annotationsToRetrieve);

      try {
        const recordIds = annotationsToRetrieve.map(
          (annotation) => annotation.externalDataId,
        );

        const categorizationOptions = await (startedLookup ??
          getCategorizationOptions({ recordIds }).unwrap());

        const updatedAnnotations = processResolvedAnnotations(
          annotationsToRetrieve,
          categorizationOptions,
        );
        return mergeAnnotations(
          allCategorizationAnnotations,
          updatedAnnotations,
        );
      } catch (error) {
        console.error("Failed to resolve external data:", error);
        notification.error({
          message: t("categorization.resolveFailedTitle"),
          description: t("categorization.resolveFailedDescription"),
        });
        const failedAnnotations = processFailedAnnotations(
          annotationsToRetrieve,
        );
        return mergeAnnotations(
          allCategorizationAnnotations,
          failedAnnotations,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getAnnotationsToRetrieve,
      setLoadingState,
      processResolvedAnnotations,
      processFailedAnnotations,
      mergeAnnotations,
      getCategorizationOptions,
      t,
    ],
  );

  return {
    startCategorizationLookup,
    fetchCategorizationAnnotationData,
    isFetchingData: isLoading,
  };
};

export default useCategorizationDataFetcher;
